import React, { useEffect, useState } from 'react';
import {
    BooleanField,
    Count,
    CreateButton,
    Datagrid,
    FieldProps,
    Filter,
    Link,
    List,
    NumberField,
    ReferenceField,
    ReferenceInput,
    SelectInput,
    TextField,
    TextInput,
    TopToolbar,
    useDataProvider,
    useRecordContext,
} from 'react-admin';
import { useNavigate, useParams } from 'react-router';
import { NoDataSection } from '../../common/NoDataSection';
import StatusTags from '../../common/StatusTags';
import { Tests } from '../../custom';
import { Modal, Tooltip } from '@mui/material';
import { Close, DocumentScanner, Edit, FileUpload } from '@mui/icons-material';
import { TestMeta } from '../tests/TestList';
import { BulkUploadSectionQuestions } from '../sections/SectionQuestionUpload';
import ClearFiltersOnRouteChange from '../../common/ClearFiltersOnRouteChange';
import { format } from 'date-fns';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';
import Button from '@mui/material/Button';
import { BulkUploadQuestionBankViaDocAction } from '../questions_bank/BulkUploadUsingDocs';
import QuestionPaper from '../../common/question-paper/QuestionPaperView';
import { useMutation, useQuery } from 'react-query';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import CommonModal from '../../common/ui/CommonModal';
import QuestionsListInput from '../sections/QuestionsListInput';
import BulkUploadCreate from '../bulk-uploads/BulkUploadCreate';

const ListActions = () => {
    const navigate = useNavigate();
    return (
        <TopToolbar>
            <CreateButton
                onClick={e => {
                    e.preventDefault();
                    navigate(`/exercises/create`);
                }}
            />
        </TopToolbar>
    );
};

const QuestionsLinkField = (props: FieldProps<Tests>) => {
    const record = useRecordContext<any>();
    if (!record) {
        return null;
    }
    return <Link to={`/exercises/${record.id}/questions_list`}>Questions</Link>;
};

const QuestionPaperButton = ({ onClick }: { onClick: () => void }) => {
    return (
        <Button onClick={onClick}>
            <DocumentScanner fontSize="large" className="pr-2" /> View Questions
        </Button>
    );
};

const ExerciseFilters = (props: any) => {
    return (
        <Filter {...props}>
            <TextInput
                variant={'outlined'}
                label="Name"
                source="name"
                alwaysOn={true}
                defaultValue=""
            />
        </Filter>
    );
};

const EditButton = ({ isTagShow, onActionClick }: any) => {
    const navigate = useNavigate();
    const record = useRecordContext();
    return (
        <Tooltip title="">
            <button
                className="w-full text-md uppercase font-semibold  flex justify-start items-center gap-2 text-left px-2 py-1"
                onClick={() => {
                    onActionClick?.();
                    navigate(`/exercises/${record?.id}/edit`);
                }}
            >
                <Edit fontSize="medium" />
                {isTagShow && 'Edit'}
            </button>
        </Tooltip>
    );
};

let currentCloseFunction: (() => void) | null = null;

const BulkUploadQbButton = ({ setShowActions }: any) => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();

    const [sectionId, setSectionId] = React.useState<number | null>(null);
    const sectionCreatedRef = React.useRef(false);
    const { data: Sections, isLoading } = useQuery(
        ['sections', 'getList', record?.id],
        () =>
            dataProvider.getList('sections', {
                filter: { testId: record?.id },
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'id', order: 'ASC' },
            }),
        { enabled: !!record?.id },
    );

    const createSectionMutation = useMutation(
        (newSectionData: any) => dataProvider.create('sections', { data: newSectionData }),
        {
            onSuccess: response => {
                setSectionId(response.data.id);
            },
            onError: error => {
                console.error('Section creation error:', error);
            },
        },
    );

    useEffect(() => {
        if (!Sections || !Array.isArray(Sections.data)) return;
        if (sectionId) return;
        if (createSectionMutation.isLoading) return;

        if (Sections.data.length > 0) {
            setSectionId(Sections.data[0].id);
        } else if (Sections.data.length === 0 && !sectionCreatedRef.current) {
            sectionCreatedRef.current = true;

            createSectionMutation.mutate({
                name: 'Section 1',
                testId: record?.id,
            });
        }
    }, [Sections, record?.id]);

    if (isLoading || !sectionId) return null;

    return (
        <BulkUploadQuestionBankViaDocAction
            sectionId={sectionId}
            path="/test"
            setShowActions={setShowActions}
        />
    );
};

const Actions = () => {
    const [showActions, setShowActions] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const record = useRecordContext();
    const [bulkUploadModal, setBulkUploadModal] = useState(false);

    useEffect(() => {
        const handleClickOutside = () => {
            setShowActions(false);
        };

        if (showActions) {
            if (currentCloseFunction && currentCloseFunction !== handleClickOutside) {
                currentCloseFunction();
            }
            currentCloseFunction = handleClickOutside;

            setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 0);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
            if (currentCloseFunction === handleClickOutside) {
                currentCloseFunction = null;
            }
        };
    }, [showActions]);

    const handleButtonClick = (e: any) => {
        e.stopPropagation();
        setShowActions(prev => !prev);
    };

    const preventClose = (e: any) => {
        e.stopPropagation();
    };

    const handleOpenModal = () => {
        setShowModal(true);
        setShowActions(false);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    return (
        <div className="relative inline-block">
            <button onClick={handleButtonClick} className="p-2 rounded-full hover:bg-gray-200">
                <MoreVertIcon />
            </button>

            {showActions && (
                <div
                    className="absolute text-md uppercase right-0 mt-2 w-60 bg-white shadow-lg rounded-lg p-2 flex flex-col justify-start text-start gap-2 z-10"
                    onClick={preventClose}
                >
                    <div className="flex w-full gap-2 justify-start text-[#4f3cc9] text-md items-center hover:bg-blue-50">
                        <BulkUploadQbButton setShowActions={setShowActions} />
                    </div>
                    <div className="flex w-full gap-2 justify-start text-[#4f3cc9] text-md items-center hover:bg-blue-50">
                        <BulkUploadSectionQuestions type={'exercise'} />
                    </div>
                    <div className="flex w-full font-semibold p-2  gap-2 justify-start text-[#4f3cc9] text-md items-center cursor-pointer hover:bg-blue-50">
                        <button
                            onClick={() => setBulkUploadModal(true)}
                            className="flex items-center uppercase gap-2"
                        >
                            <FileUpload fontSize="medium" /> Bulk Upload
                        </button>
                    </div>
                    <div className="hover:bg-blue-50 w-full">
                        <QuestionPaperButton onClick={handleOpenModal} />
                    </div>
                    <div className="flex  gap-2 justify-start text-[#4f3cc9] text-md cursor-pointer hover:bg-blue-50">
                        <EditButton isTagShow={true} onActionClick={() => setShowActions(false)} />
                    </div>
                    <div className="flex w-full gap-2 justify-start text-[#4f3cc9] text-md items-center cursor-pointer hover:bg-blue-50">
                        <ConfirmDeleteButton setShowActions={setShowActions} />
                    </div>
                </div>
            )}

            <Modal
                open={showModal}
                onClose={handleCloseModal}
                aria-labelledby="question-paper-modal"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div className="bg-white rounded-lg shadow-2xl min-w-[75vw] max-w-[80vw] min-h-[90vh] max-h-[95vh] relative flex flex-col">
                    <div className="flex-1 overflow-auto ">
                        {record && <QuestionPaper testId={record.id} />}
                    </div>
                    <div className="sticky bottom-0 z-40 p-2 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-t flex justify-end">
                        <Button variant="contained" onClick={handleCloseModal} aria-label="Close">
                            Close <Close />
                        </Button>
                    </div>
                </div>
            </Modal>

            <CommonModal
                title="Questions Bulk Upload"
                open={bulkUploadModal}
                setOpen={setBulkUploadModal}
            >
                <BulkUploadCreate selectedType="EXERCISE_QUESTION_UPLOAD" />
            </CommonModal>
        </div>
    );
};

const FormattedStartEndTime = () => {
    const record = useRecordContext<any>();
    return record ? (
        <div className={'flex flex-col text-sm space-y-1'}>
            {record.startTime && (
                <div className={'flex items-center space-x-2'}>
                    <div
                        className={
                            'w-4 h-4 flex flex-col items-center justify-center rounded-full bg-blue-200'
                        }
                    >
                        S
                    </div>
                    <div className={'whitespace-nowrap'}>
                        {format(new Date(record.startTime), 'dd/MM/yyyy hh:mm a')}
                    </div>
                </div>
            )}
            {record.endTime && (
                <div className={'flex items-center  space-x-2'}>
                    <div
                        className={
                            'w-4 h-4 flex flex-col items-center justify-center rounded-full bg-red-200'
                        }
                    >
                        E
                    </div>
                    <div className={'whitespace-nowrap'}>
                        {format(new Date(record.endTime), 'dd/MM/yyyy hh:mm a')}
                    </div>
                </div>
            )}
        </div>
    ) : null;
};

FormattedStartEndTime.defaultProps = {
    source: 'startTime',
    label: 'Start - End',
};
const ExercisesList = () => {
    const navigate = useNavigate();
    return (
        <List
            sort={{ field: 'created_at', order: 'DESC' }}
            empty={
                <NoDataSection
                    resource={'tests'}
                    onCreate={() => {
                        navigate(`/exercise/create`);
                    }}
                />
            }
            resource={'tests'}
            actions={<ListActions />}
            filter={{
                type: 'EXERCISE',
            }}
            filters={<ExerciseFilters />}
        >
            <ClearFiltersOnRouteChange />
            <Datagrid bulkActionButtons={false}>
                <TextField source="name" />
                <StatusTags source="status" label={'Status'} />
                <BooleanField source="isDemo" />
                <NumberField source="displayOrder" />
                <TestMeta />
                <FormattedDateTimeField source="createdAt" />
                <FormattedStartEndTime source={'startTime'} />
                <QuestionsLinkField />
                <Actions />
            </Datagrid>
        </List>
    );
};

export default ExercisesList;
